import type { PriceDocument, PriceRule, ServiceKind } from "@woodpecker/protocol";
import { Y, type TextRef } from "../i18n";

/** wpCredit base units → wp (6 decimals), rounded to 2 places for display. */
export const ONE_WP = 1_000_000n;
export function wpOf(base: bigint): number {
  return Math.round(Number(base) / 1e4) / 100;
}
export function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
export function sum(...n: number[]): number {
  return round2(n.reduce((a, b) => a + b, 0));
}
/** "1 wpCredit = {n} token": tokens per wpCredit from the token's rate (base units per token). */
export function perCredit(rate: bigint): number {
  if (rate <= 0n) return 0;
  return Math.round((1e6 / Number(rate)) * 100) / 100;
}
/** wp per token from the rate. */
export function wpPerToken(rate: bigint): number {
  return Number(rate) / 1e6;
}
export function short(address: string): string {
  return address.length > 12 ? `${address.slice(0, 6)}…${address.slice(-4)}` : address;
}
export function shortTx(tx: string): string {
  return tx.length > 12 ? `${tx.slice(0, 6)}…${tx.slice(-4)}` : tx;
}

/** The mock's price summary of a service: the first paid route, as "fixed · 1 tLLM per call" etc. */
export interface PriceSummary {
  mode: "fixed" | "tiered" | "metered";
  amount?: number;
  max: number;
  unit?: string;
  meter?: string;
  measure?: string;
  path?: string;
}

export function priceSummaryOf(doc: PriceDocument): PriceSummary {
  if (doc.summary)
    return {
      mode: doc.summary.mode,
      max: doc.summary.max,
      amount: doc.summary.mode === "fixed" ? doc.summary.max : undefined,
      unit: doc.summary.unit,
    };
  const rule = Object.values(doc.routes)[0];
  const unit = doc.kinds.includes("runner")
    ? "round"
    : doc.kinds.includes("memory")
      ? "commit"
      : doc.kinds.includes("host")
        ? "operation"
        : "call";
  if (!rule) return { mode: "fixed", amount: 1, max: 1, unit };
  return summaryOfRule(rule, unit);
}

export function summaryOfRule(rule: PriceRule, unit?: string): PriceSummary {
  if (rule.form === "fixed") return { mode: "fixed", amount: rule.price, max: rule.price, unit };
  if (rule.form === "tiered")
    return {
      mode: "tiered",
      max: Math.max(0, ...rule.tiers.map(([, p]) => p)),
      measure: rule.measure,
      path: rule.measure === "bytes" ? undefined : rule.path,
      unit,
    };
  return { mode: "metered", max: rule.maxPerCall, meter: rule.meter, unit };
}

export function priceText(p: PriceSummary, token: string): TextRef {
  const unit = Y(`unit.${p.unit ?? "call"}`);
  if (p.mode === "tiered") {
    return Y("price.tiered", {
      measure:
        p.measure === "bytes" ? Y("measure.bytes") : Y(`measure.${p.measure ?? "length"}`, { path: p.path ?? "" }),
      max: p.max,
      token,
      unit,
    });
  }
  if (p.mode === "metered") {
    if (p.meter) return Y("price.meteredMeter", { meter: p.meter, max: p.max, token, unit });
    if (p.unit) return Y("price.meteredUnit", { max: p.max, token, unit });
    return Y("price.metered", { max: p.max, token });
  }
  return Y("price.fixed", { amount: p.amount ?? p.max, token, unit });
}

export function maxOf(p: PriceSummary): number {
  return p.mode === "fixed" ? (p.amount ?? p.max) : p.max;
}

export function batchText(batch: number | "turn" = 1): TextRef {
  return batch === "turn" ? Y("batch.turn") : Y("batch.n", { n: batch });
}

export const KIND_ORDER: ServiceKind[] = ["runner", "memory", "model", "tool", "host"];

export function nowTime(): string {
  return new Date().toTimeString().slice(0, 8);
}

export function timeText(iso: string): TextRef | string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const t = d.toTimeString().slice(0, 5);
  const today = new Date();
  const same = d.toDateString() === today.toDateString();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (same) return Y("time.today", { t });
  if (d.toDateString() === yesterday.toDateString()) return Y("time.yesterday", { t });
  return `${d.toISOString().slice(0, 10)} ${t}`;
}
